import XeroEntitiesTable from './XeroEntitiesTable';
import OnboardingResultsBanner from './OnboardingResultsBanner';
import OnboardingSummaryBanner from './OnboardingSummaryBanner';
import XeroEntitiesFormSubmit from './XeroEntitiesFormSubmit';
import { XeroTenantConnection } from '../../../types/xero';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Entity } from '../../../types/entity';
import { useAllEntities } from '../../../queries/use-entities';
import { binarySearchEntityName } from '../utils';

export type OnboardingXeroEntity = {
    xeroEntity: XeroTenantConnection;
    entityId?: string;
    isSelected: boolean;
};

export type OnboardingXeroEntities = {
    connections: OnboardingXeroEntity[];
    globalSelected: boolean;
};

type XeroEntitiesFormProps = {
    xeroUserId: string;
    connections: XeroTenantConnection[];
    onCancel: () => void;
};

function XeroEntitiesForm({ xeroUserId, connections, onCancel }: XeroEntitiesFormProps): JSX.Element {
    const [onboardAlert, setOnboardAlert] = useState<{ successMessages?: string[]; errorMessages?: string[] }>({});

    const form = useForm<OnboardingXeroEntities>({
        defaultValues: {
            connections: connections.map((c) => ({ xeroEntity: c, isSelected: true })),
            globalSelected: true,
        },
    });

    const { data, fetchNextPage, isLoading, isFetchingNextPage } = useAllEntities({
        page: 1,
        search: '',
        sort: 'sort[dir]=asc&sort[field]=name',
        filter: 'isArchived=false&integrations[xero]=false',
    });
    const entities = data?.pages?.reduce<Entity[]>((acc, p) => [...acc, ...p.entities], []) ?? [];
    const total = data?.pages?.at(0)?.totalItems;

    /**
     * Fetch next page of entities if entities count does not equal to total items count
     * Only update form default value when every pages of entities is fetched
     *
     * Dependencies of [form,entities] are not needed because form default value should only be updated when length of entities equals to the total
     */
    useEffect(() => {
        if (total !== undefined && entities.length < total) {
            fetchNextPage();
        } else if (total !== undefined && entities.length === total) {
            form.setValue(
                'connections',
                connections.map((c) => ({
                    xeroEntity: c,
                    isSelected: true,
                    entityId: binarySearchEntityName(entities, c.tenantName),
                }))
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchNextPage, entities.length, total, connections]);

    return (
        <>
            <FormProvider {...form}>
                {onboardAlert.successMessages || onboardAlert.errorMessages ? (
                    <OnboardingResultsBanner
                        onSuccessAlertClose={(): void => setOnboardAlert((prev) => ({ ...prev, successMessages: undefined }))}
                        onErrorAlertClose={(): void => setOnboardAlert((prev) => ({ ...prev, errorMessages: undefined }))}
                        errorMessages={onboardAlert?.errorMessages}
                        successMessages={onboardAlert?.successMessages}
                    />
                ) : (
                    <OnboardingSummaryBanner />
                )}
                <hgroup className="mb-4">
                    <h2 className="text-lg text-gray-900 font-medium">Onboard Xero Entities</h2>
                    <p className="text-sm text-gray-600 max-w-[100ch]">
                        Bulk onboard your recently connected Xero entities into Cimplico Workpapers using the table below. Select the
                        entities you want to onboard, and optionally, map them to an existing entity in Cimplico Workpapers.
                    </p>
                </hgroup>
                <form className="flex flex-col gap-8">
                    <XeroEntitiesTable entities={entities} isLoading={isLoading || isFetchingNextPage || entities.length !== total} />
                    <XeroEntitiesFormSubmit
                        xeroUserId={xeroUserId}
                        tenantConnections={connections}
                        onOnboardResult={setOnboardAlert}
                        onCancel={onCancel}
                    />
                </form>
            </FormProvider>
        </>
    );
}

export default XeroEntitiesForm;
